/**
 * CSRF double-submit echo (cookie-session-auth-canon.md primitive 5).
 *
 * The session issuer sets a NON-HttpOnly `csrf-token` cookie beside the
 * HttpOnly session cookie; this SPA echoes its value as the `X-CSRF-Token`
 * header on POST, PUT, PATCH and DELETE, and a backend that enforces the pair
 * refuses a cookie-authenticated mutation without it. The value is a
 * per-session nonce and not a credential, so reading it carries no
 * no-browser-token-storage concern.
 *
 * One reader for the app. `api/client.ts` re-exports it for the openapi-fetch
 * clients and the raw-fetch services that already import it from there;
 * `stores/auth.ts` uses it for sign-out, which is a raw fetch outside those
 * clients (GH #4888). The store cannot import `api/client.ts`, which imports
 * the store.
 */

/** The request header that carries the echo. */
export const CSRF_HEADER_NAME = 'X-CSRF-Token';

/** The value of the `csrf-token` cookie, or null when the cookie is absent. */
export function readCsrfCookie(): string | null {
  if (typeof document === 'undefined') return null;
  for (const pair of document.cookie.split(';')) {
    const idx = pair.indexOf('=');
    if (idx === -1) continue;
    if (pair.slice(0, idx).trim() === 'csrf-token') {
      return decodeURIComponent(pair.slice(idx + 1).trim());
    }
  }
  return null;
}

/** `{ 'X-CSRF-Token': <value> }` when the cookie is present, else `{}`. */
export function csrfHeaders(): Record<string, string> {
  const csrf = readCsrfCookie();
  return csrf ? { [CSRF_HEADER_NAME]: csrf } : {};
}
